// src/components/auth/SessionsModal.tsx
// Login History / Device Tracking (self-service) — lists this user's active
// sessions (device/IP/created/expires) and lets them revoke one at a time.
// Uses authService.getSessions(), which existed already but was never called
// from any component before this phase — see Phase 5 plan.
import { useDispatch } from 'react-redux';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Monitor } from 'lucide-react';
import { showToast } from 'store/slices/toastSlice';
import { authService, Session } from 'services/authService';
import { parseUserAgent } from 'utils/parseUserAgent';

function formatDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    + ', ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
}

export default function SessionsModal({ onClose }: { onClose: () => void }) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<Session[]>({
    queryKey: ['auth', 'sessions'],
    queryFn: authService.getSessions,
  });
  const sessions: Session[] = data ?? [];

  const revoke = useMutation({
    mutationFn: (tokenId: string) => authService.revokeSession(tokenId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auth', 'sessions'] });
      dispatch(showToast('🖥️ Session revoked'));
    },
    onError: () => dispatch(showToast('❌ Failed to revoke session')),
  });

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-[var(--om-panel)] border border-[var(--om-border)] rounded-2xl w-[90vw] max-w-md max-h-[80vh] flex flex-col shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--om-border)] flex-shrink-0">
          <span className="inline-flex items-center gap-1.5 text-sm font-bold text-[var(--om-text)]"><Monitor className="w-4 h-4" strokeWidth={2} /> Active Sessions</span>
          <button onClick={onClose} className="text-[var(--om-text-dim)] hover:text-white text-lg leading-none">×</button>
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          {isLoading ? (
            <p className="text-[11px] text-[var(--om-text-dim)] text-center py-8">Loading...</p>
          ) : sessions.length === 0 ? (
            <p className="text-[11px] text-[var(--om-text-dim)] text-center py-8">No active sessions</p>
          ) : (
            <div className="space-y-1.5">
              {sessions.map(s => (
                <div key={s.tokenId} className="bg-[var(--om-card)] border border-[var(--om-border)] rounded-xl p-3 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-[12px] text-[var(--om-text)] font-semibold truncate">{parseUserAgent(s.userAgent)}</p>
                    <p className="text-[11px] text-[var(--om-text-dim)] font-mono">{s.ipAddress || 'Unknown IP'}</p>
                    <p className="text-[11px] text-[var(--om-text-dim)]">Since {formatDate(s.createdAt)}</p>
                  </div>
                  <button
                    onClick={() => revoke.mutate(s.tokenId)}
                    disabled={revoke.isPending}
                    className="px-2.5 py-1.5 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-[11px] font-bold hover:bg-red-500/20 transition-all flex-shrink-0 disabled:opacity-50"
                  >
                    Revoke
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
